/**
 * Tiny request de-duplicator for GET endpoints.
 *
 * - Concurrent calls with the same key share one in-flight promise
 *   (kills StrictMode double-mounts and multiple consumers hitting the
 *   same list endpoint on load).
 * - The resolved value is briefly reused so back-to-back mounts don't refetch.
 *
 * Mutations should call clearRequestCache(prefix) so the next read is fresh.
 */
const inflight = new Map();
const cache = new Map();

export function dedupedRequest(key, factory, { ttlMs = 3000 } = {}) {
  const entry = cache.get(key);
  if (entry && Date.now() - entry.at < ttlMs) {
    return Promise.resolve(entry.value);
  }
  if (inflight.has(key)) {
    return inflight.get(key);
  }

  const promise = Promise.resolve()
    .then(factory)
    .then((value) => {
      if (inflight.get(key) === promise) {
        cache.set(key, { value, at: Date.now() });
        inflight.delete(key);
      }
      return value;
    })
    .catch((error) => {
      if (inflight.get(key) === promise) inflight.delete(key);
      throw error;
    });

  inflight.set(key, promise);
  return promise;
}

export function clearRequestCache(prefix) {
  if (!prefix) {
    cache.clear();
    inflight.clear();
    return;
  }
  for (const key of [...inflight.keys()]) {
    if (key.startsWith(prefix)) inflight.delete(key);
  }
  for (const key of [...cache.keys()]) {
    if (key.startsWith(prefix)) cache.delete(key);
  }
}
